const BASE_URL = process.env.REACT_APP_API_URL;
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;

const TOKEN_KEY = 'kio_access_token';
const REFRESH_KEY = 'kio_refresh_token';
const EXPIRY_KEY = 'kio_token_expiry';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

function isRemembered() {
  return Boolean(localStorage.getItem(TOKEN_KEY));
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(EXPIRY_KEY);
  clearApiCache();
}

function storeTokens({ access_token, refresh_token, expires_in }, remember = true) {
  const storage = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  other.removeItem(TOKEN_KEY); other.removeItem(REFRESH_KEY); other.removeItem(EXPIRY_KEY);
  storage.setItem(TOKEN_KEY, access_token);
  if (refresh_token) storage.setItem(REFRESH_KEY, refresh_token);
  if (expires_in) storage.setItem(EXPIRY_KEY, String(Date.now() + expires_in * 1000));
  clearApiCache();
}

// ── GET cache ─────────────────────────────────────────────────────────────────
// In-memory cache for GET responses (TTL) + deduplication of identical
// concurrent GETs. Any mutation (POST/PUT/PATCH/DELETE, upload) or auth change
// clears it, so reads can never serve data older than the last local write.
// Per-call opt-out: apiFetch(path, { noCache: true }) ; TTL override: { cacheTTL: ms }.
const GET_CACHE_TTL = 30_000;
const getCache = new Map();      // path -> { ok, status, statusText, headers, body, expires }
const inflightGets = new Map();  // path -> Promise<entry>

export function clearApiCache(prefix) {
  // Prefix form: an import only invalidates the products reads, instead of
  // wiping every cached GET of the whole app every few seconds.
  if (!prefix) return getCache.clear();
  for (const key of getCache.keys()) {
    if (key.startsWith(prefix)) getCache.delete(key);
  }
}

function responseFromEntry(entry) {
  return {
    ok: entry.ok,
    status: entry.status,
    statusText: entry.statusText,
    headers: { get: (name) => entry.headers[String(name).toLowerCase()] ?? null },
    json: async () => JSON.parse(entry.body),
    text: async () => entry.body,
    clone() { return responseFromEntry(entry); },
  };
}

async function doRefresh() {
  const remembered = isRemembered();
  const storage = remembered ? localStorage : sessionStorage;
  const refresh = storage.getItem(REFRESH_KEY);
  if (!refresh) throw new Error('no_refresh');

  const res = await fetch(`${BASE_URL}/connect/o/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'refresh_token',
      refresh_token: refresh,
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
    }),
  });

  if (!res.ok) throw new Error('refresh_failed');
  const data = await res.json();
  storeTokens(data, remembered);
  return data.access_token;
}

async function requestWithAuth(path, options = {}, _retry = true) {
  let token = getStoredToken();

  const expiry = parseInt(localStorage.getItem(EXPIRY_KEY) || sessionStorage.getItem(EXPIRY_KEY) || '0', 10);
  if (token && expiry && Date.now() > expiry - 60_000) {
    try { token = await doRefresh(); } catch { /* use existing token */ }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && _retry) {
    try {
      await doRefresh();
      return requestWithAuth(path, options, false);
    } catch {
      clearTokens();
      throw new Error('session_expired');
    }
  }

  return res;
}

export async function apiFetch(path, options = {}, _retry = true) {
  const method = (options.method || 'GET').toUpperCase();

  // Mutations: hit the network, then drop every cached GET.
  if (method !== 'GET') {
    const res = await requestWithAuth(path, options, _retry);
    clearApiCache();
    return res;
  }

  // Opt-outs: aborted requests keep their own semantics, noCache bypasses fully.
  if (options.noCache || options.signal) {
    return requestWithAuth(path, options, _retry);
  }

  const hit = getCache.get(path);
  if (hit && hit.expires > Date.now()) return responseFromEntry(hit);

  const pending = inflightGets.get(path);
  if (pending) return pending.then(responseFromEntry);

  const promise = (async () => {
    const res = await requestWithAuth(path, options, _retry);
    const entry = {
      ok: res.ok,
      status: res.status,
      statusText: res.statusText,
      headers: Object.fromEntries(res.headers.entries()),
      body: await res.text(),
      expires: Date.now() + (options.cacheTTL ?? GET_CACHE_TTL),
    };
    if (res.ok) getCache.set(path, entry);
    return entry;
  })();

  inflightGets.set(path, promise);
  try {
    return responseFromEntry(await promise);
  } finally {
    inflightGets.delete(path);
  }
}

export async function apiRevokeToken() {
  const token = getStoredToken();
  if (!token) return;
  try {
    await fetch(`${BASE_URL}/connect/o/revoke_token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: CLIENT_ID,
        grant_type: 'password',
        token,
      }),
    });
  } catch { /* best-effort: ignore network errors on logout */ }
}

export async function apiConvertGoogleToken(googleAccessToken, remember = true) {
  let res;
  try {
    res = await fetch(`${BASE_URL}/connect/auth/convert-token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'convert_token',
        client_id: CLIENT_ID,
        backend: 'google-oauth2',
        token: googleAccessToken,
      }),
    });
  } catch {
    throw new Error('no_internet');
  }

  if (!res.ok) {
    let data;
    try { data = await res.json(); } catch { /* ignore */ }
    if (data?.settings?.userExist) {
      throw new Error('user_exist');
    }
    let msg = 'google_auth_failed';
    try {
      msg = data?.error_description || data?.error || data?.detail || msg;
    } catch { /* keep default */ }
    throw new Error(msg);
  }

  const tokens = await res.json();
  storeTokens(tokens, remember);
  return tokens;
}

export async function apiLogin(identifier, password, remember = true) {
  const res = await fetch(`${BASE_URL}/connect/o/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'password',
      client_id: CLIENT_ID,
      username: identifier.toLowerCase(),
      password,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    console.error('[apiLogin] 400 body:', text);
    let msg = 'Identifiant ou mot de passe incorrect';
    try { msg = JSON.parse(text).error_description || JSON.parse(text).error || msg; } catch {}
    throw new Error(msg);
  }

  const tokens = await res.json();
  storeTokens(tokens, remember);
  return tokens;
}

export async function fetchCurrentUser() {
  const res = await apiFetch('/connect/user/?format=json');
  if (!res.ok) throw new Error('Impossible de récupérer le profil utilisateur');
  return res.json();
}

export async function fetchDevices() {
  const res = await apiFetch('/connect/device/?format=json');
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de récupérer les terminaux'));
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Supprime un terminal (DELETE /connect/device/, même convention que les autres DELETE : body { id }). */
export async function apiDeleteDevice(id) {
  const res = await apiFetch('/connect/device/?format=json', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de supprimer le terminal'));
}

export async function fetchUserStores() {
  const res = await apiFetch('/store/?format=json');
  if (res.status === 404) return [];
  if (!res.ok) throw new Error('Impossible de récupérer les boutiques');
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Thème actif de la boutique (GET /store/{slug}/theme/). */
export async function fetchStoreTheme(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/theme/?format=json`);
  if (!res.ok) throw new Error('Impossible de récupérer le thème');
  return res.json();
}

/** Lance la reconstruction / installation du thème (POST /store/{slug}/theme-rebuild/). */
export async function apiPostThemeRebuild(storeSlug, installedThemeId) {
  const res = await apiFetch(`/store/${storeSlug}/theme-rebuild/?format=json`, {
    method: 'POST',
    body: JSON.stringify({ theme_id: installedThemeId }),
    noCache: true,
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de mettre à jour le thème'));
  try { await res.json(); } catch { /* corps vide ou non-JSON */ }
}

/**
 * Poll GET /store/{slug}/theme/ until predicate returns true.
 * Returns the last fetched theme (matched or not).
 */
export async function pollStoreThemeUntil(
  storeSlug,
  predicate,
  { intervalMs = 2000, maxAttempts = 60 } = {},
) {
  let last = null;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    last = await fetchStoreTheme(storeSlug);
    if (predicate(last)) return last;
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }
  return last;
}

/** Reconstruit le thème installé puis recharge le détail via GET /store/{slug}/theme/. */
export async function apiRebuildStoreTheme(storeSlug, installedThemeId) {
  await apiPostThemeRebuild(storeSlug, installedThemeId);
  return fetchStoreTheme(storeSlug);
}

/** Détail boutique — settings, domaine par défaut, custom_domain, etc. */
export async function fetchStoreBySlug(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/?format=json`);
  if (!res.ok) throw new Error('Impossible de récupérer la boutique');
  return res.json();
}

export async function fetchStoreDomains(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/domains/?format=json`);
  if (!res.ok) throw new Error('Impossible de récupérer les domaines');
  return res.json();
}

export async function apiUpdateStoreCustomDomain({ id, slug, customDomain }) {
  const res = await apiFetch('/store/?format=json', {
    method: 'PUT',
    body: JSON.stringify({
      id,
      slug,
      custom_domain: [customDomain],
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de connecter le domaine'));
  try { return await res.json(); } catch { return null; }
}

/** Déconnecte le domaine personnalisé (DELETE /store/ avec custom_domain : seul le domaine est retiré). */
export async function apiDeleteStoreCustomDomain({ id, customDomain }) {
  if (!customDomain) throw new Error('Aucun domaine à déconnecter');
  const res = await apiFetch('/store/?format=json', {
    method: 'DELETE',
    body: JSON.stringify({ id, custom_domain: [customDomain] }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de déconnecter le domaine'));
}

/** Met à jour les informations générales de la boutique (PUT /store/). */
export async function apiUpdateStore(payload) {
  const res = await apiFetch('/store/?format=json', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de mettre à jour la boutique'));
  try { return await res.json(); } catch { return null; }
}

/** Supprime une boutique (DELETE /store/). */
export async function apiDeleteStore(id) {
  const res = await apiFetch('/store/?format=json', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de supprimer la boutique'));
}

export async function searchAvailableDomains(query) {
  const q = encodeURIComponent(String(query || '').trim());
  const res = await apiFetch(`/services/domain/?domain=${q}&format=json`);
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de rechercher les domaines'));
  return res.json();
}

export function buildDomainCheckoutUrl(domain, currency = 'DZD', price) {
  const params = new URLSearchParams({
    type: 'domain',
    domain,
    currency,
  });
  if (price != null && price !== '') params.set('price', String(price));
  return `/checkout?${params.toString()}`;
}

export async function updateStoreSettings(storeSlug, payload) {
  const res = await apiFetch(`/store/${storeSlug}/settings/?format=json`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder les paramètres'));
  try { return await res.json(); } catch { return null; }
}

export async function fetchStoreSettings(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/settings/?format=json`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les paramètres'));
  try { return await res.json(); } catch { return null; }
}

export async function fetchStoreSeo(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/seo/?format=json`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger le SEO'));
  try { return await res.json(); } catch { return null; }
}

export async function updateStoreSeo(storeSlug, payload) {
  const res = await apiFetch(`/store/${storeSlug}/seo/?format=json`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder le SEO'));
  try { return await res.json(); } catch { return null; }
}

export async function apiCreateStore(payload) {
  const res = await apiFetch('/store/?format=json', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const message = await parseApiError(res.clone(), 'Impossible de créer la boutique');
    const error = new Error(message);
    // Per-field validation errors (e.g. { name: [...], slug: [...] }) so callers can
    // attribute each error to its own form field instead of guessing from the message.
    try {
      const data = await res.json();
      if (data && typeof data === 'object' && !data.detail && !data.message) error.fields = data;
    } catch { /* no JSON body */ }
    throw error;
  }
  return res.json();
}

export async function fetchStoreCustomers(storeSlug, { page = 1 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    format: 'json',
  });
  const res = await apiFetch(`/store/${storeSlug}/customers/?${params.toString()}`);
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de recuperer les clients'));
  const data = await res.json();
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return {
    count: typeof data?.count === 'number' ? data.count : results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results,
  };
}

export async function fetchStoreCustomersStats(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/customers-stats/?format=json`);
  if (res.status === 404) return { totalCustomers: 0, totalNew: 0, totalNormal: 0, totalVip: 0 };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les statistiques clients'));
  const data = await res.json();
  return {
    totalCustomers: data.total_customers ?? 0,
    totalNew: data.total_new ?? 0,
    totalNormal: data.total_normal ?? 0,
    totalVip: data.total_vip ?? 0,
  };
}

function extractInviteLink(data) {
  const link = data?.invitation_link ?? data?.invitationLink;
  if (typeof link === 'string' && link.trim()) return link.trim();
  if (Array.isArray(link) && typeof link[0] === 'string' && link[0].trim()) return link[0].trim();
  return null;
}

function extractInviteDetails(data) {
  if (Array.isArray(data) && data.length && typeof data[0] === 'string') {
    return data[0];
  }
  const raw = data?.Details ?? data?.detail ?? data?.email?.[0] ?? data?.phone_number?.[0];
  if (Array.isArray(raw)) return typeof raw[0] === 'string' ? raw[0] : null;
  return typeof raw === 'string' ? raw : null;
}

function isInviteSuccess(detailsStr) {
  if (!detailsStr) return true;
  const lower = detailsStr.toLowerCase();
  return lower.includes('sent') || lower.includes('succes') || lower.includes('success');
}

function isAlreadyInvitedMessage(detailsStr) {
  if (!detailsStr) return false;
  return detailsStr.toLowerCase().includes('already invited');
}

export async function inviteStoreCustomer(storeSlug, { email = null, phoneNumber = null } = {}) {
  const res = await apiFetch(`/store/${storeSlug}/customers/?format=json`, {
    method: 'POST',
    body: JSON.stringify({
      email: email || null,
      phone_number: phoneNumber || null,
    }),
  });

  let data = {};
  try { data = await res.json(); } catch { /* keep empty */ }

  const invitationLink = extractInviteLink(data);
  const detailsStr = extractInviteDetails(data);

  if (res.status === 201) {
    return {
      invitationLink,
      details: detailsStr,
      alreadyInvited: false,
      data,
    };
  }

  if (res.status === 400 && invitationLink) {
    return {
      invitationLink,
      details: detailsStr,
      alreadyInvited: true,
      data,
    };
  }

  if (!res.ok) {
    throw new Error(detailsStr || await parseApiError(res, 'Impossible d\'envoyer l\'invitation'));
  }

  if (detailsStr && !isInviteSuccess(detailsStr) && !invitationLink) {
    throw new Error(detailsStr);
  }

  return {
    invitationLink,
    details: detailsStr,
    alreadyInvited: isAlreadyInvitedMessage(detailsStr),
    data,
  };
}

export async function updateStoreCustomer(storeSlug, customerId, partialBody) {
  const res = await apiFetch(
    `/store/${storeSlug}/customers/?format=json`,
    { method: 'PUT', body: JSON.stringify({ id: customerId, ...partialBody }) }
  );
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de modifier le client'));
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** Met à jour le téléphone du user lié au client (PUT /customers/ avec phone_number). */
export async function updateStoreCustomerPhone(storeSlug, customer, phoneNumber) {
  const customerId = storeCustomerPutId(customer);
  if (!customerId) throw new Error('Identifiant client introuvable.');
  let phone = String(phoneNumber ?? '').trim();
  if (!phone) throw new Error('Numéro de téléphone requis.');
  if (!phone.startsWith('+')) phone = `+${phone.replace(/^\+/, '')}`;
  return updateStoreCustomer(storeSlug, customerId, { phone_number: phone });
}

export async function fetchStoreCustomerInvitations(storeSlug, { page = 1 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    format: 'json',
  });
  const res = await apiFetch(`/store/${storeSlug}/customers-invitation/?${params.toString()}`);
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de recuperer les invitations'));
  const data = await res.json();
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return {
    count: typeof data?.count === 'number' ? data.count : results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results,
  };
}

export async function fetchStoreCustomerInvitationStats(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/customers-invitation-stats/?format=json`);
  if (res.status === 404) return { totalInvitations: 0, totalPending: 0, totalAccepted: 0 };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les statistiques invitations'));
  const data = await res.json();
  return {
    totalInvitations: data.total_invitations ?? 0,
    totalPending: data.total_pending ?? 0,
    totalAccepted: data.total_accepted ?? 0,
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Identifiant client pour PUT /store/{slug}/customers/ (UUID Customer.id). */
export function storeCustomerPutId(customer) {
  if (!customer) return null;
  const id = customer.id ?? customer.uuid;
  return id != null ? String(id) : null;
}

/** Résout l'id PK customer_status pour une catégorie (vip / normal / new) depuis l'API boutique. */
export function customerStatusIdByCategory(statuses, category) {
  const cat = String(category || '').toLowerCase();
  return (statuses ?? []).find((s) => {
    const slug = String(s.slug ?? '').toLowerCase();
    const name = String(s.name ?? '').toLowerCase();
    if (cat === 'vip') return slug === 'vip' || name === 'vip';
    if (cat === 'new') return slug === 'new' || name === 'new';
    return slug === 'normal' || name === 'normal';
  })?.id ?? null;
}

/** Identifiant client pour /orders/customers/{id}/ (UUID utilisateur). */
export function customerOrdersApiId(customer) {
  if (!customer) return null;
  const candidates = [
    customer.user?.uuid,
    customer.user?.id,
    customer.uuid,
    customer.id,
  ];
  for (const cand of candidates) {
    if (cand != null && UUID_RE.test(String(cand))) return String(cand);
  }
  return customer.id != null ? String(customer.id) : null;
}

export async function fetchStoreCustomerStatuses(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/customer-status/?format=json`);
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les statuts clients'));
  const data = await res.json();
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
}

export async function fetchCustomerOrders(storeSlug, customerId, { page = 1, pageSize } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    format: 'json',
  });
  if (pageSize != null) params.set('page_size', String(pageSize));
  const res = await apiFetch(
    `/store/${storeSlug}/orders/customers/${customerId}/?${params.toString()}`
  );
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les commandes du client'));
  const data = await res.json();
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return {
    count: typeof data?.count === 'number' ? data.count : results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results,
  };
}

export async function fetchStoreContactMessages(storeSlug, { page = 1 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    format: 'json',
  });
  const res = await apiFetch(`/store/${storeSlug}/contact/?${params.toString()}`);
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de recuperer les messages'));
  const data = await res.json();
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return {
    count: typeof data?.count === 'number' ? data.count : results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results,
  };
}

export async function fetchStoreContactStats(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/contact-stats/?format=json`);
  if (res.status === 404) return { totalMessages: 0, totalUnread: 0, totalRead: 0 };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les statistiques messages'));
  const data = await res.json();
  return {
    totalMessages: data.total_messages ?? 0,
    totalUnread: data.total_unread ?? 0,
    totalRead: data.total_read ?? 0,
  };
}

export async function fetchStoreContactMessage(storeSlug, messageId) {
  const res = await apiFetch(`/store/${storeSlug}/contact/${messageId}/?format=json`);
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger le message'));
  return res.json();
}

export async function apiSignup({ email, firstName, lastName, password, phoneNumber, username, recaptchaToken }) {
  const res = await fetch(`${BASE_URL}/connect/signup/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: email || null,
      first_name: firstName,
      last_name: lastName,
      password,
      phone_number: phoneNumber || null,
      username,
      'g-recaptcha-response': recaptchaToken,
    }),
  });

  if (!res.ok) {
    let msg = 'Impossible de créer le compte';
    try {
      const data = await res.json();
      if (typeof data === 'object' && data !== null) {
        const parts = Object.entries(data).map(([key, value]) => {
          const text = Array.isArray(value) ? value.join(', ') : String(value);
          return `${key}: ${text}`;
        });
        if (parts.length) msg = parts.join(' · ');
      }
    } catch { /* keep default */ }
    throw new Error(msg);
  }

  return res.json();
}

async function parseApiError(res, fallback) {
  let msg = fallback;
  try {
    const data = await res.json();
    if (typeof data === 'object' && data !== null) {
      if (data.detail) return String(data.detail);
      if (data.message) return String(data.message);
      const parts = Object.entries(data).map(([key, value]) => {
        const text = Array.isArray(value) ? value.join(', ') : String(value);
        return `${key}: ${text}`;
      });
      if (parts.length) return parts.join(' · ');
    }
  } catch { /* keep default */ }
  return msg;
}

export async function apiResendVerification({ email, phoneNumber }) {
  const res = await apiFetch('/connect/verification/?format=json', {
    method: 'POST',
    body: JSON.stringify({
      confirmation_code_email: null,
      confirmation_code_phone: null,
      email: email || null,
      phone_number: phoneNumber || null,
      resend: true,
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de renvoyer le code'));
  return res.json();
}

export async function apiSubmitVerification({ code, email, phoneNumber, contactMode }) {
  const res = await apiFetch('/connect/verification/?format=json', {
    method: 'POST',
    body: JSON.stringify({
      confirmation_code_email: contactMode === 'email' ? code : null,
      confirmation_code_phone: contactMode === 'phone' ? code : null,
      email: email || null,
      phone_number: phoneNumber || null,
      resend: false,
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Code de vérification incorrect'));
  return res.json();
}

async function putCurrentUser(overrides = {}) {
  const current = await fetchCurrentUser();
  const body = {
    username: current.username,
    first_name: overrides.first_name ?? current.first_name,
    last_name: overrides.last_name ?? current.last_name,
    email: overrides.email !== undefined ? overrides.email : current.email,
    phone_number: overrides.phone_number !== undefined ? overrides.phone_number : current.phone_number,
    avatar_src: overrides.avatar_src !== undefined ? overrides.avatar_src : current.avatar_src,
  };

  const res = await apiFetch('/connect/user/?format=json', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de mettre à jour le profil'));
  return res.json();
}

export async function apiUpdateProfile({ firstName, lastName, email, phoneNumber }) {
  return putCurrentUser({
    first_name: firstName,
    last_name: lastName,
    email: email !== undefined ? (email || null) : undefined,
    phone_number: phoneNumber !== undefined ? (phoneNumber || null) : undefined,
  });
}

/**
 * Adds a phone number to the signed-in account (partial PUT /connect/user/).
 * kio-api keeps it pending (confirmation.last_phone_number) and texts a code;
 * user.phone_number is only set once that code is verified.
 * Error messages: 'phone_taken' (number already on another account),
 * 'phone_invalid' (rejected by PhoneNumberValidator / max_length),
 * 'sms_failed' (code could not be sent: 400 {"Details": "..."}).
 */
export async function apiSetPhoneNumber(phoneNumber) {
  const res = await apiFetch('/connect/user/?format=json', {
    method: 'PUT',
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  if (res.ok) return res.json();
  if (res.status === 400) {
    let data = null;
    try { data = await res.json(); } catch { /* not JSON */ }
    const phoneErrors = data?.phone_number;
    if (phoneErrors) {
      const messages = Array.isArray(phoneErrors) ? phoneErrors : [phoneErrors];
      // DRF UniqueValidator: "user with this phone number already exists."
      const taken = messages.some((m) => /already exists/i.test(String(m)));
      throw new Error(taken ? 'phone_taken' : 'phone_invalid');
    }
    if (data?.Details) throw new Error('sms_failed');
  }
  throw new Error(await parseApiError(res, 'Impossible de mettre à jour le profil'));
}

export async function apiChangePassword({ oldPassword, newPassword }) {
  const res = await apiFetch('/connect/change-password/?format=json', {
    method: 'PUT',
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de changer le mot de passe'));
  return res.json();
}

export async function apiCreateAddress({ name, street, street2, cityId, stateId, countryId, isDefault }) {
  const body = {
    name,
    country: Number(countryId),
    state: Number(stateId),
    city: Number(cityId),
    street,
    street2: street2 || '',
  };
  if (isDefault !== undefined) body.default = Boolean(isDefault);
  const res = await apiFetch('/connect/user/addresses/?format=json', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible d\'ajouter l\'adresse'));
  try { return await res.json(); } catch { return null; }
}

export async function apiUpdateAddress(id, { name, street, street2, cityId, stateId, countryId, isDefault, ownerId }) {
  const body = {
    id,
    name,
    street,
    street2: street2 || null,
    city: Number(cityId),
    state: Number(stateId),
    country: Number(countryId),
    default: Boolean(isDefault),
  };
  if (ownerId != null) body.owner = ownerId;
  const res = await apiFetch('/connect/user/addresses/?format=json', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de mettre à jour l\'adresse'));
  try { return await res.json(); } catch { return null; }
}

export async function apiDeleteAddress(id) {
  const res = await apiFetch('/connect/user/addresses/?format=json', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de supprimer l\'adresse'));
}

export async function apiUpload(path, formData, _retry = true) {
  let token = getStoredToken();

  const expiry = parseInt(localStorage.getItem(EXPIRY_KEY) || sessionStorage.getItem(EXPIRY_KEY) || '0', 10);
  if (token && expiry && Date.now() > expiry - 60_000) {
    try { token = await doRefresh(); } catch { /* use existing token */ }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (res.status === 401 && _retry) {
    try {
      await doRefresh();
      return apiUpload(path, formData, false);
    } catch {
      clearTokens();
      throw new Error('session_expired');
    }
  }

  clearApiCache();
  return res;
}

export async function fetchStoreFilesStats(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/files-stats/?format=json`);
  if (res.status === 404) {
    return { totalFiles: 0, totalImages: 0, totalDocuments: 0, totalMedia: 0, storageUsed: '', storageUsedBytes: 0, storageLimit: '', storageLimitBytes: 0, storagePercentage: 0 };
  }
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les statistiques'));
  const data = await res.json();
  return {
    totalFiles: data.total_files ?? 0,
    totalImages: data.total_images ?? 0,
    totalDocuments: data.total_documents ?? 0,
    totalMedia: data.total_media ?? 0,
    storageUsed: data.storage_used ?? '',
    storageUsedBytes: data.storage_used_bytes ?? 0,
    storageLimit: data.storage_limit ?? '',
    storageLimitBytes: data.storage_limit_bytes ?? 0,
    storagePercentage: data.storage_percentage ?? 0,
  };
}

export async function fetchStoreFiles(storeSlug, { type, page = 1, pageSize } = {}) {
  const params = new URLSearchParams({ page: String(page), format: 'json' });
  if (type !== undefined) params.set('type', type);
  if (pageSize != null) params.set('page_size', String(pageSize));
  const res = await apiFetch(`/store/${storeSlug}/files-filter/?${params.toString()}`);
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger les fichiers'));
  const data = await res.json();
  const results = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return {
    count: typeof data?.count === 'number' ? data.count : results.length,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results,
  };
}

export async function uploadStoreFile(storeSlug, file, { name, type } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name ?? file.name);
  if (type) formData.append('type', type);
  const res = await apiUpload(`/store/${storeSlug}/files/?format=json`, formData);
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de télécharger le fichier'));
  return res.json();
}

export async function deleteStoreFile(storeSlug, id) {
  const res = await apiFetch(`/store/${storeSlug}/files/?format=json`, {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de supprimer le fichier'));
}

export async function fetchFacebookPixel(storeSlug) {
  const res = await apiFetch(`/marketing/${storeSlug}/facebook/pixel/?format=json`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger la configuration Pixel'));
  try { return await res.json(); } catch { return null; }
}

export async function saveFacebookPixel(storeSlug, { name, pixelId, accessToken }) {
  const res = await apiFetch(`/marketing/${storeSlug}/facebook/pixel/?format=json`, {
    method: 'POST',
    body: JSON.stringify({
      name: name || null,
      pixel_id: pixelId,
      access_token: accessToken,
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder le Pixel Facebook'));
  try { return await res.json(); } catch { return null; }
}

export async function fetchSheetCredentials() {
  const res = await apiFetch('/connect/sheet_credentials/?format=json');
  if (res.status === 404) return null;
  if (!res.ok) return null;
  try { return await res.json(); } catch { return null; }
}

export async function putSheetCredentials({ accessToken, refreshToken }) {
  const res = await apiFetch('/connect/sheet_credentials/?format=json', {
    method: 'PUT',
    body: JSON.stringify({
      credentials: {
        'access-token-sheet':  accessToken,
        'refresh-token-sheet': refreshToken,
      },
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder les credentials Google'));
  try { return await res.json(); } catch { return null; }
}

export async function fetchStoreSheet(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/sheet/?format=json`);
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger le sheet lié'));
  const data = await res.json();
  return Array.isArray(data) ? data : (data ? [data] : []);
}

export async function saveStoreSheet(storeSlug, { sheetId, isActive = true }) {
  const res = await apiFetch(`/store/${storeSlug}/sheet/?format=json`, {
    method: 'POST',
    body: JSON.stringify({ sheet_id: sheetId, is_active: Boolean(isActive) }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder le sheet'));
  try { return await res.json(); } catch { return null; }
}

export async function updateStoreSheet(storeSlug, { sheetId, isActive }) {
  const body = {};
  if (sheetId   !== undefined) body.sheet_id  = sheetId;
  if (isActive  !== undefined) body.is_active = Boolean(isActive);
  const res = await apiFetch(`/store/${storeSlug}/sheet/?format=json`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de mettre à jour le sheet'));
  try { return await res.json(); } catch { return null; }
}

export async function deleteStoreSheet(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/sheet/?format=json`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de dissocier le sheet'));
}

export async function fetchGoogleDriveSheets(accessToken) {
  const q = encodeURIComponent("mimeType='application/vnd.google-apps.spreadsheet'");
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name,modifiedTime)&orderBy=modifiedTime%20desc`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (res.status === 401) throw new Error('token_expired');
  if (!res.ok) throw new Error('Impossible de lister les Google Sheets');
  const data = await res.json();
  return Array.isArray(data.files) ? data.files : [];
}

export async function createGoogleSheet(name, accessToken) {
  const res = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ properties: { title: name } }),
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('token_expired');
    throw new Error('Impossible de créer le Google Sheet');
  }
  return res.json();
}

export async function fetchGA4(storeSlug) {
  const res = await apiFetch(`/store/${storeSlug}/ga4/?format=json`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de charger la configuration GA4'));
  try { return await res.json(); } catch { return null; }
}

export async function saveGA4(storeSlug, { gaId, active }) {
  const res = await apiFetch(`/store/${storeSlug}/ga4/?format=json`, {
    method: 'POST',
    body: JSON.stringify({ ga_id: gaId, active: Boolean(active) }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de sauvegarder la configuration GA4'));
  try { return await res.json(); } catch { return null; }
}

export async function apiContactSupport({ email, firstName, lastName, subject, contents }) {
  const res = await apiFetch('/services/contact/', {
    method: 'POST',
    body: JSON.stringify({
      email,
      first_name: firstName,
      last_name: lastName,
      subject,
      contents,
    }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible d\'envoyer le message'));
  try { return await res.json(); } catch { return null; }
}

export async function apiResetPasswordByEmail(email) {
  const res = await fetch(`${BASE_URL}/connect/reset-password/mail/?admin=true`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible d\'envoyer le lien de réinitialisation'));
  try { return await res.json(); } catch { return null; }
}

export async function apiResetPasswordByPhone(phoneNumber) {
  const res = await fetch(`${BASE_URL}/connect/reset-password/phone/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible d\'envoyer le code de réinitialisation'));
  try { return await res.json(); } catch { return null; }
}

export async function apiVerifyResetCode(phoneNumber, code) {
  const res = await fetch(`${BASE_URL}/connect/reset-password/phone/verif-code/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, phone_number: phoneNumber }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Code de vérification invalide'));
  try { return await res.json(); } catch { return null; }
}

export async function apiConfirmResetPassword(phoneNumber, code, newPassword) {
  const res = await fetch(`${BASE_URL}/connect/reset-password/phone/confirm/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, phone_number: phoneNumber, new_password: newPassword }),
  });
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de réinitialiser le mot de passe'));
  try { return await res.json(); } catch { return null; }
}

export async function apiUploadProfilePicture(file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', file.name);
  formData.append('file-type', 'media');

  const res = await apiUpload('/connect/profile/upload-picture/?format=json', formData);
  if (!res.ok) throw new Error(await parseApiError(res, 'Impossible de télécharger la photo de profil'));

  const uploadData = await res.json();
  const avatarSrc = uploadData.file;
  if (!avatarSrc) throw new Error('Réponse upload invalide');

  await putCurrentUser({ avatar_src: avatarSrc });
  return fetchCurrentUser();
}

export async function apiAcceptStaffInvitation(token) {
  const res = await apiFetch(
    `/connect/staff/invitations/${encodeURIComponent(token)}/`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(await parseApiError(res, "Impossible d'accepter l'invitation"));
  return res.json();
}

export async function apiStaffSignup({ firstName, lastName, username, email, password, staffToken }) {
  const res = await fetch(
    `${BASE_URL}/connect/signup/?staff_token=${encodeURIComponent(staffToken)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        username,
        email,
        password,
      }),
    },
  );
  if (!res.ok) {
    let msg = 'Impossible de créer le compte';
    try {
      const data = await res.json();
      if (typeof data === 'object' && data !== null) {
        const parts = Object.entries(data).map(([key, value]) => {
          const text = Array.isArray(value) ? value.join(', ') : String(value);
          return `${key}: ${text}`;
        });
        if (parts.length) msg = parts.join(' · ');
      }
    } catch { /* keep default */ }
    throw new Error(msg);
  }
  return res.json();
}
